import { useEffect, useRef, useState } from "react";
import { Zap, Loader2, Play, Square, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { API, formatApiError } from "@/lib/api";

const LABEL = { colère: "Colère", peur: "Peur", joie: "Joie", tristesse: "Tristesse", murmure: "Murmure", calme: "Calme" };

// Pick a Surge patch per emotion present in the scene, preview each stinger, then insert them on the timeline (1 credit).
export default function StingerPanel({ dialogueId, emotions, onInserted, seq }) {
  const [choice, setChoice] = useState({});
  const [quick, setQuick] = useState({ favorites: [], recent: [] });
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [busy, setBusy] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => { api.get("/synth/patches/quick").then(({ data }) => setQuick(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!q.trim()) { setFound([]); return; }
    const t = setTimeout(() => api.get("/synth/patches", { params: { q, limit: 20 } }).then(({ data }) => setFound(data.patches)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const options = [...quick.favorites, ...quick.recent.filter((r) => !quick.favorites.some((f) => f.id === r.id)), ...found.filter((f) => !quick.favorites.some((x) => x.id === f.id) && !quick.recent.some((x) => x.id === f.id))];

  const preview = (emo) => {
    if (playing === emo) { audioRef.current?.pause(); setPlaying(null); return; }
    const pid = choice[emo] && choice[emo] !== "auto" ? choice[emo] : "";
    const a = new Audio(`${API}/synth/stinger-preview?emotion=${encodeURIComponent(emo)}&patch_id=${encodeURIComponent(pid)}&v=${Date.now()}`);
    audioRef.current?.pause();
    audioRef.current = a;
    a.onended = () => setPlaying(null);
    a.onerror = () => { setPlaying(null); toast.error("Aperçu indisponible."); };
    a.play().then(() => setPlaying(emo)).catch(() => toast.error("Lecture bloquée par le navigateur."));
  };

  const insert = async () => {
    setBusy(true);
    try {
      const patches = Object.fromEntries(Object.entries(choice).filter(([, v]) => v && v !== "auto"));
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/stingers`, { patches });
      toast.success(`${data.count} stinger${data.count > 1 ? "s" : ""} Surge XT inséré${data.count > 1 ? "s" : ""} sur la timeline`);
      onInserted?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Stingers impossibles."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-3 space-y-2" data-testid={`stinger-panel-${seq}`}>
      <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono text-amber-400"><Zap size={12} /> STINGERS SURGE XT — un patch par émotion · aperçu gratuit · insertion 1 crédit
        <div className="relative ml-auto"><Search size={11} className="absolute left-2 top-2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="chercher un patch…" className="h-7 pl-6 pr-2 w-44 rounded-md bg-background border border-border text-[11px] normal-case font-sans text-foreground" data-testid={`stinger-search-${seq}`} /></div>
      </div>
      {emotions.length === 0 && <div className="text-xs text-muted-foreground">Aucune réplique émotionnelle : taguez des émotions (colère, peur, joie…) dans le compositeur.</div>}
      {emotions.map((emo) => (
        <div key={emo} className="flex flex-wrap items-center gap-2" data-testid={`stinger-row-${emo}`}>
          <span className="text-xs font-semibold w-20">{LABEL[emo] || emo}</span>
          <Select value={choice[emo] || "auto"} onValueChange={(v) => setChoice({ ...choice, [emo]: v })}>
            <SelectTrigger className="h-8 text-xs w-60 bg-background border-border" data-testid={`stinger-patch-${emo}`}><SelectValue /></SelectTrigger>
            <SelectContent className="glass border-border max-h-64">
              <SelectItem value="auto">Patch auto (selon l'émotion)</SelectItem>
              {options.map((p) => <SelectItem key={p.id} value={p.id}>{p.favorite ? "★ " : ""}{p.name} · {p.category}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={() => preview(emo)} className="h-8 gap-1.5 text-amber-400" data-testid={`stinger-preview-${emo}`}>
            {playing === emo ? <Square size={12} /> : <Play size={12} />} {playing === emo ? "Stop" : "Aperçu"}
          </Button>
        </div>
      ))}
      <div className="flex justify-end">
        <Button size="sm" onClick={insert} disabled={busy || emotions.length === 0} className="h-8 bg-amber-500 text-black hover:bg-amber-400 gap-1.5" data-testid={`stinger-insert-${seq}`}>
          {busy ? <Loader2 className="animate-spin" size={13} /> : <Zap size={13} />} Insérer sur la timeline · 1 cr
        </Button>
      </div>
    </div>
  );
}
